import { Box, Container, Grid, Typography } from "@mui/material";
import useResponsive from "../../../hooks/useResponsive";
import { PoolShape } from "../../../constants/pool";
import { useFormContext } from "react-hook-form";
import { FirstStepFormData } from "../../../schemas/validationSchemas";
import { FormNumberField } from "../../common";
import { PoolMaterial } from "../../../context/types";

export const PoolDimensionsForm = () => {
  const { isSm, isXs } = useResponsive();
  const isMobile = isSm || isXs;
  const { control, watch } = useFormContext<FirstStepFormData>();

  
  const shapeValue = watch("shape");
  const lengthValue = watch("length");
  const widthValue = watch("width");
  const diameterValue = watch("diameter");
  const depthValue = watch("depth");
  const surfaceAreaValue = watch("surfaceArea");
  const poolMaterialValue = watch("poolMaterial");

  const currentShape = shapeValue;

  
  const requiresVolumeAdjustment =
    poolMaterialValue === PoolMaterial.FIBERGLASS ||
    poolMaterialValue === PoolMaterial.PLASTICLINER;

  
  const calculatePoolVolume = () => {
    if (!depthValue || depthValue <= 0) return 0;

    let volume = 0;

    switch (currentShape) {
      case PoolShape.SQUARE:
        if (!lengthValue || lengthValue <= 0) return 0;
        volume = Math.pow(lengthValue, 2) * depthValue * 1000; 
        break;

      case PoolShape.RECTANGULAR:
        if (!lengthValue || lengthValue <= 0 || !widthValue || widthValue <= 0)
          return 0;
        volume = lengthValue * widthValue * depthValue * 1000; 
        break;

      case PoolShape.ROUND:
        if (!diameterValue || diameterValue <= 0) return 0;
        volume = Math.PI * Math.pow(diameterValue / 2, 2) * depthValue * 1000; 
        break;

      case PoolShape.KIDNEY:
      default:
        if (!surfaceAreaValue || surfaceAreaValue <= 0) return 0;
        volume = surfaceAreaValue * depthValue * 1000; 
        break;
    }

    
    if (requiresVolumeAdjustment) {
      volume = volume * 0.95;
    }

    return volume;
  };

  const poolVolume = calculatePoolVolume();
  const formattedVolume =
    poolVolume > 0 ? Math.round(poolVolume).toLocaleString() : "------";

  
  const volumeDisplay =
    requiresVolumeAdjustment && poolVolume > 0
      ? `${formattedVolume}*`
      : formattedVolume;

  const InputsArray =
    currentShape === PoolShape.SQUARE
      ? [
          <FormNumberField
            key="length-square"
            name="length"
            control={control}
            placeholder="Length (m)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
          <FormNumberField
            key="depth-square"
            name="depth"
            control={control}
            placeholder="Average Depth (m)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
        ]
      : currentShape === PoolShape.RECTANGULAR
      ? [
          <FormNumberField
            key="length-rect"
            name="length"
            control={control}
            placeholder="Length (m)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
          <FormNumberField
            key="width-rect"
            name="width"
            control={control}
            placeholder="Width (m)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
          <FormNumberField
            key="depth-rect"
            name="depth"
            control={control}
            placeholder="Average Depth (m)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
        ]
      : currentShape === PoolShape.ROUND
      ? [
          <FormNumberField
            key="diameter-round"
            name="diameter"
            control={control}
            placeholder="Diameter (m)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
          <FormNumberField
            key="depth-round"
            name="depth"
            control={control}
            label="Average Depth (m)"
            placeholder="Average Depth (m)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
        ]
      : [
          <FormNumberField
            key="surface-other"
            name="surfaceArea"
            control={control}
            placeholder="Surface area (m²)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
          <FormNumberField
            key="depth-other"
            name="depth"
            control={control}
            placeholder="Average Depth (m)"
            sx={{
              color: "black",
              padding: "10px 16px",
              background: "#f2f2f2",
              width: `${isMobile ? "100%" : "300px"}`,
            }}
          />,
        ];

  const spacing = isMobile ? 4 : 8;

  return (
    <Box mt={spacing} py={4} sx={{ background: "#dedede" }}>
      <Container>
        <Typography textAlign={isMobile ? "start" : "center"}>
          Pool Dimensions (in metres)
        </Typography>
        <Grid
          container
          mt={4}
          alignItems={"center"}
          flexDirection={"row"}
          justifyContent={"center"}
          gap={4}
        >
          {InputsArray}
        </Grid>
        <Typography
          mt={5}
          fontSize={32}
          fontWeight={"bold"}
          textAlign={"center"}
        >
          Estimated Pool Volume = {volumeDisplay} Litres
        </Typography>
        {requiresVolumeAdjustment && poolVolume > 0 && (
          <Typography
            textAlign={"center"}
            fontSize={14}
            color="text.secondary"
            mt={1}
          >
            *Volume was adjusted based on pool material
          </Typography>
        )}
        <Typography textAlign={"center"}>Pool volume calculations</Typography>
      </Container>
    </Box>
  );
};
